import {
  ARH_XSL_FILENAME,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  SCHEMA_VERSION,
} from "core/consts";
import { getStaticAsset } from "core/model/config";
import { b64DecodeUnicode } from "core/model/utils/base64";
import { getErrorMessage } from "core/model/utils/errors";
import { openPdfInNewWindow } from "core/model/utils/urls";
import { AHBMessageResponse, QueryProgressActive } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useGetAHBResponse } from "onprem/hooks";
import { PDFDocument } from "pdf-lib";
import { useMemo, useState } from "react";
import { useTranslations } from "translations";

const AHB_RESPONSE_PDF_PAGE_COUNT = 1;
const PDF_GENERATION_TIMEOUT_MS = 30000; // 30 seconds
const HTML2PDF_SCALE = 1.0;
const HTML2PDF_IMAGE_QUALITY = 0.75;

// Cache for XSL stylesheet to avoid repeated fetches
let xslCache: string | null = null;

function sanitizeXml(text: string): string {
  return text
    .replace(/^\uFEFF/, "") // Remove BOM
    .replace(/^\s+/, "") // Trim leading whitespace
    .replace(/\r\n/g, "\n") // Normalize line endings
    .replace(/<!DOCTYPE[^>]*>/gi, "") // Remove DTDs (can be used in XXE)
    .replace(/<\?xml[^>]*\?>/gi, ""); // Remove XML declarations (can conflict)
}

function validateXmlDocument(doc: Document, context: string): void {
  const parserError = doc.querySelector("parsererror");
  if (parserError) {
    throw new Error(
      `Invalid ${context}: ${parserError.textContent || "Parsing failed"}`,
    );
  }
}

/** Response PDF uses 1.1.0 XSL until 2.0 response format is defined. */
async function getCachedXSL(): Promise<string> {
  if (xslCache) {
    return xslCache;
  }

  const xslFilename = ARH_XSL_FILENAME[SCHEMA_VERSION.V1_1_0];
  const xslUrl = getStaticAsset(xslFilename);
  const res = await fetch(xslUrl);
  if (!res.ok) throw new Error("Failed to load XSL");
  xslCache = await res.text();
  return xslCache;
}

export async function transformXmlToPdf({
  endPage,
  startPage,
  xml,
  xsl,
}: {
  endPage?: number;
  startPage?: number;
  xml: string;
  xsl: string;
}) {
  // Early validation to fail fast
  if (startPage && endPage && startPage > endPage) {
    throw new Error("startPage cannot be greater than endPage");
  }
  const html2pdf = (await import("html2pdf.js")).default;
  const parser = new DOMParser();

  const sanitizedXml = sanitizeXml(xml);
  const sanitizedXsl = sanitizeXml(xsl);

  const xmlDoc = parser.parseFromString(sanitizedXml, "text/xml");
  validateXmlDocument(xmlDoc, "XML");

  const xslDoc = parser.parseFromString(sanitizedXsl, "text/xml");
  validateXmlDocument(xslDoc, "XSL");

  const xsltProcessor = new XSLTProcessor();
  xsltProcessor.importStylesheet(xslDoc);
  const htmlFragment = xsltProcessor.transformToFragment(xmlDoc, document);

  if (!htmlFragment) {
    throw new Error("XSL transformation failed to produce HTML fragment");
  }

  // Create a temporary div and append the HTML fragment
  const tempDiv = document.createElement("div");
  const styleTag = document.createElement("style");
  styleTag.textContent = `
   body { 
        width: 100vw !important;
        min-width: 100vw !important;
        padding: 0 !important;
        margin: 0 !important;
    }
`;
  tempDiv.prepend(styleTag);
  tempDiv.appendChild(htmlFragment);

  // Only set non-default values for isolation and performance
  const fullPdf = await html2pdf()
    .set({
      html2canvas: {
        foreignObjectRendering: false, // Disable foreign object rendering
        removeContainer: true, // Remove container after rendering
        scale: HTML2PDF_SCALE,
      },
      image: { quality: HTML2PDF_IMAGE_QUALITY, type: "jpeg" },
    })
    .from(tempDiv)
    .outputPdf("arraybuffer");

  // If no slicing is needed, return the PDF directly
  if (!startPage && !endPage) {
    return fullPdf;
  }

  // Optimize: If we need all pages, return directly
  if (startPage === 1 && !endPage) {
    return fullPdf;
  }

  // Otherwise, slice the PDF as needed
  const originalPdf = await PDFDocument.load(fullPdf);
  const totalPages = originalPdf.getPageCount();

  // Optimize: If endPage is beyond total pages, adjust it
  const actualEndPage = endPage ? Math.min(endPage, totalPages) : totalPages;
  const actualStartPage = startPage ?? 1;

  // Optimize: If we're requesting all pages, return the original
  if (actualStartPage === 1 && actualEndPage === totalPages) {
    return fullPdf;
  }

  const slicedPdf = await PDFDocument.create();

  for (let i = actualStartPage - 1; i < actualEndPage; i++) {
    const [copiedPage] = await slicedPdf.copyPages(originalPdf, [i]);
    slicedPdf.addPage(copiedPage);
  }

  const slicedPdfBytes = await slicedPdf.save();

  return slicedPdfBytes;
}

export function useAHBResponseToPDF() {
  const toast = useToast();
  const translations = useTranslations();
  const [progress, setProgress] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [, getAHBResponse] = useGetAHBResponse();

  // Memoize the XSL loading to avoid repeated fetches
  const xslPromise = useMemo(() => getCachedXSL(), []);

  const renderAndOpenAHBPdf = async (
    response_id: AHBMessageResponse["response_id"],
  ) => {
    setLoadingId(response_id);
    setProgress(QUERY_PROGRESS_PENDING);

    // Add timeout for long-running operations
    const timeoutId = setTimeout(() => {
      console.warn(
        "PDF generation taking longer than expected for response_id:",
        response_id,
      );
    }, PDF_GENERATION_TIMEOUT_MS); // 30 seconds

    try {
      const base64Xml = await getAHBResponse(response_id);
      if (!base64Xml) {
        throw new Error("Could not fetch AHB response content");
      }

      const cleaned = base64Xml.trim().replace(/(^")|("$)/g, "");
      const xml = b64DecodeUnicode(cleaned);

      if (!xml) {
        throw new Error("Could not decode AHB response content");
      }

      // Use cached XSL
      const xsl = await xslPromise;

      const pdfBytes = await transformXmlToPdf({
        endPage: AHB_RESPONSE_PDF_PAGE_COUNT,
        xml,
        xsl,
      });
      openPdfInNewWindow(pdfBytes);
    } catch (err) {
      toast({ color: "danger", message: translations.general.error });
      console.error("Failed to open PDF in new tab:", getErrorMessage(err));
    } finally {
      clearTimeout(timeoutId);
      setProgress(QUERY_PROGRESS_NOT_STARTED);
      setLoadingId(null);
    }
  };

  return [
    (response_id: AHBMessageResponse["response_id"]) =>
      response_id === loadingId ? progress : QUERY_PROGRESS_NOT_STARTED,
    renderAndOpenAHBPdf,
  ] as const;
}
